import type { IFallbackImageProps } from 'atoms/FallbackImage'
import type { ITextBoxProps, TextBoxTypes } from 'atoms/TextBox'
import type { ITextBlockProps } from './TextBlock'
import type {
  TextBlockAlignItems,
  TextBlockJustifyContent,
} from './TextBlock.h'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { PosterSC } from 'atoms/ImagePoster'
import { PopupAdapterSC } from 'blocks/PopupAdapter'
import { COLOR, INDENTS } from 'vars/index'
import { ShowMorePositions, TextBlockDirection } from './TextBlock.h'

const TOTAL_FLEX_BASIS: number = 100

export const LogoSC = styled.div``

export const ContainerWithImageSC = styled.div<{
  direction: TextBlockDirection
  justifyContent: TextBlockJustifyContent
  alignItems: TextBlockAlignItems
  rowGap: ITextBlockProps['rowGap']
  backgroundLogo: COLOR
  rounded: boolean
  posterUrl?: string
  height?: number
  width?: number
  fallbackImageBackgroundColor?: IFallbackImageProps['background']
}>`
  --imagePaddings: 11px;
  display: flex;
  justify-content: ${({ justifyContent }) => justifyContent || 'initial'};
  align-items: ${({ alignItems }) => alignItems || 'initial'};
  flex-direction: ${({ direction }) => direction || 'initial'};
  gap: ${({ rowGap }) => `var(${rowGap})`};

  & ${LogoSC} {
    padding: var(--imagePaddings);
    background: ${({
      backgroundLogo,
      fallbackImageBackgroundColor = COLOR.SURFACE_OPACITY_LIGHT_10,
    }) =>
      backgroundLogo
        ? `var(${backgroundLogo})`
        : `var(${fallbackImageBackgroundColor})`};
    border-radius: ${({ rounded }) => (rounded ? '50%' : 0)};
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    display: flex;
    justify-content: center;
    align-items: center;

    & ${PosterSC} {
      object-fit: contain;
      width: ${({ width }) => `calc(${width}px - var(--imagePaddings) * 2)`};
      height: ${({ height }) => `calc(${height}px - var(--imagePaddings) * 2)`};
    }
  }
`

export const Wrapper = styled.div<ITextBlockProps>`
  display: ${({ direction }) =>
    direction === TextBlockDirection.INLINE ? 'block' : 'flex'};
  flex-direction: ${({ direction }) => direction || 'initial'};
  align-items: ${({ alignItems }) => alignItems ?? 'initial'};
  justify-content: ${({ justifyContent }) => justifyContent ?? 'initial'};
  flex-grow: ${({ flexGrow }) => flexGrow ?? 'initial'};
  width: ${({ width }) => (width ? `${width}%` : 'auto')};
  gap: ${({ rowGap }) => `var(${rowGap})`};
  max-width: 100%;

  & > :nth-child(1) {
    width: ${({ flexBasis, direction }) =>
      direction === TextBlockDirection.ROW ? `${flexBasis}%` : 'auto'};
    flex: ${({ flexBasisAuto, direction }) =>
      direction === TextBlockDirection.ROW && flexBasisAuto
        ? `0 0 auto`
        : 'unset'};
  }

  & > :not(${ButtonSC}):nth-child(2) {
    width: ${({ flexBasis, direction }) =>
      flexBasis && direction === TextBlockDirection.ROW
        ? TOTAL_FLEX_BASIS - flexBasis + '%'
        : 'auto'};
  }
`

export const TextBlockWithGradientSC = styled.div<{
  maxHeight?: number
  maxLines?: number
  showMorePosition: string
  showOverflow?: boolean
  lineHeight?: number
  btnWidth?: number
}>`
  position: relative;
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : 'auto')};
  overflow: ${({ showOverflow }) => (showOverflow ? 'hidden' : 'auto')};
  display: flex;
  flex-direction: column;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${({ lineHeight }) => `${lineHeight}px`};
    background: var(${COLOR.MAIN_GRADIENT_0});
    display: ${({ showOverflow, showMorePosition }) =>
      showOverflow && showMorePosition === ShowMorePositions.Under
        ? 'block'
        : 'none'};
  }

  padding-right: ${({ btnWidth }) => `${btnWidth! + 10}px`};
  // 10px indentation between button and text

  > span {
    flex: 0 0 auto;

    > span:first-child {
      padding-right: var(${INDENTS.INDENT_10});
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none; // firefox
`

export const TextBlockWithShowMoreSC = styled.div<{
  width?: number
  showMorePosition: ShowMorePositions
  colorDefault?: ITextBoxProps['color']
}>`
  width: ${({ width }) => (width ? `${width}%` : 'auto')};
  position: relative;
  color: ${({ colorDefault }) =>
    colorDefault ? `var(${colorDefault})` : `inherit`};

  ${ButtonSC} {
    position: ${({ showMorePosition }) =>
      showMorePosition === ShowMorePositions.Inline ? 'absolute' : 'relative'};
    right: ${({ showMorePosition }) =>
      showMorePosition === ShowMorePositions.Inline ? '0' : 'unset'};
    bottom: ${({ showMorePosition }) =>
      showMorePosition === ShowMorePositions.Inline ? '0' : 'unset'};
  }

  ${PopupAdapterSC} {
    > ${ButtonSC} {
      // position: absolute;
      position: ${({ showMorePosition }) =>
        showMorePosition === ShowMorePositions.Inline
          ? 'absolute'
          : 'relative'};
      right: ${({ showMorePosition }) =>
        showMorePosition === ShowMorePositions.Inline ? '0' : 'unset'};
      bottom: ${({ showMorePosition }) =>
        showMorePosition === ShowMorePositions.Inline ? '0' : 'unset'};
    }
  }
`

export const StrokeHeightChecker = styled.div<{ font: TextBoxTypes }>`
  font: ${({ font }) => `var(${font})`};
  width: fit-content;
`
